/**
 * La garde de la salle du design system : elle ne s'ouvre que dans une salle qui porte le label
 * `events-panel` (tech-derisk, point 4 A). Le fil du design system lit les signaux qu'écrit l'écouteur
 * global (`LKEvents`), et cet écouteur ne les écrit en cartes que sous ce label : sans lui, le fil
 * serait vide et les snackbars de l'ancien code s'afficheraient par-dessus.
 *
 * Le label se lit exactement comme l'ancien `hasFeature` (`data/utility/Utils.js`) : une sous-chaîne de
 * `version_ft` seule (le serveur y recopie `version`) ; une salle connue sans `version_ft` n'a pas le label,
 * puisque l'écouteur n'y écrirait aucune carte. Une séance pas encore lue ouvre le design system : les 1 029 salles actives
 * portent le label (lecture du 30/10) et toute salle créée le reçoit (`LiveRoom::defaultVersionFor`) ;
 * attendre la lecture ferait basculer la salle d'un écran à l'autre en pleine séance.
 */
import { isTutorCandidate, selectTutorDsReading } from "../../../../../../featureFlags/tutorDsFF"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"

export const EVENTS_PANEL_LABEL = "events-panel"

type ApiLiveRoom = { version_ft?: string | null } | null | undefined
type GuardState = { liveMode?: { liveSessionId?: number | string | null } }
type AccountWithSessions = { live_sessions?: { id?: number | string; live_room?: ApiLiveRoom }[] | null }

/** La salle porte-t-elle ce label ? `undefined` quand la salle n'est pas connue. */
export function liveRoomHasLabel(room: ApiLiveRoom, label: string): boolean | undefined {
    if (room == null) return undefined
    return (room.version_ft ?? "").includes(label)
}

/** La salle du live courant s'ouvre-t-elle au design system ? */
export function tutorLiveRoomOpensOnDesignSystem(state: unknown): boolean {
    const typed = state as GuardState | undefined
    const liveSessionId = typed?.liveMode?.liveSessionId
    if (liveSessionId == null) return true
    const account = selectAccount(state as Parameters<typeof selectAccount>[0]) as AccountWithSessions | null
    const session = account?.live_sessions?.find((candidate) => String(candidate?.id) === String(liveSessionId))
    return liveRoomHasLabel(session?.live_room, EVENTS_PANEL_LABEL) !== false
}

/**
 * Le compte est dans la salle du tuteur du design system : `tutor_ds` lu allumé, pas candidat (sa candidature
 * garde l'ancien écran de la salle, `tutorSpaceBridge`), et une salle qui s'y ouvre. Ce qui ne vaut que pour
 * la nouvelle salle et vit hors d'elle (le repli de caméra de `PrecallDeviceSync`) le lit ici, pas sur le
 * seul interrupteur.
 */
export function tutorIsInDesignSystemRoom(state: unknown): boolean {
    if (selectTutorDsReading(state) !== "on") return false
    const account = selectAccount(state as Parameters<typeof selectAccount>[0]) as { roles?: unknown } | null
    return !isTutorCandidate(account?.roles) && tutorLiveRoomOpensOnDesignSystem(state)
}
